import { useState } from "react";
import { ClipboardList } from "lucide-react";
import api from "../api/client";
import JobPrefill from "./JobPrefill";

/**
 * Empty dashboard state.
 *
 * Activation is a first-session event: almost everyone who logs an application
 * does so within minutes of signing up, and almost nobody returns. So the empty
 * state has to be the thing that gets someone started, not a label pointing at
 * a form.
 *
 * The flow is: type a company you applied to, pick the role, done. The
 * application is created directly rather than opening the panel, so the first
 * one costs two clicks instead of a six-field form.
 *
 * onManual falls back to the full panel, and onCreated refreshes the list.
 */
export default function DashboardEmptyState({ onManual, onCreated }) {
  const [company, setCompany] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const createFromJob = async (job) => {
    setCreating(true);
    setError(null);
    try {
      await api.post("/applications", {
        company: job.company || company,
        job_title: job.job_title,
        job_url: job.job_url || null,
        location_city: job.location_city || null,
        location_region: job.location_region || null,
        location_country: job.location_country || null,
        // Recording something already submitted, so today is the sensible
        // default. Editable afterwards.
        applied_at: new Date().toISOString(),
      });
      setCompany("");
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail ?? "Could not add that application.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="bg-white dark:bg-brand-dark-surface border border-dashed border-gray-200 dark:border-white/10 rounded-2xl p-8 sm:p-12">
      <div className="max-w-md mx-auto text-center">
        <ClipboardList className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4 mx-auto" />

        <h2 className="text-lg font-bold mb-2">
          Where have you applied recently?
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
          Add one and Joborigo starts tracking where it stands, so you are not
          left guessing.
        </p>

        <input
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Company name"
          disabled={creating}
          className="w-full px-3 py-2.5 rounded-md border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg text-sm focus:outline-none focus:ring-2 focus:ring-brand-violet/30 dark:focus:ring-brand-violet-on-dark/30 focus:border-brand-violet dark:focus:border-brand-violet-on-dark"
        />

        {/* Left-aligned, because the results list reads badly centred */}
        <div className="text-left">
          <JobPrefill company={company} onSelect={createFromJob} />
        </div>

        {error && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</p>
        )}

        {/* Always available. Prefill only covers companies with a public ATS,
            so manual entry can never be a second-class path. */}
        <button
          onClick={onManual}
          disabled={creating}
          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 mt-6"
        >
          Or enter the details yourself
        </button>
      </div>
    </div>
  );
}
